import * as Sentry from '@sentry/react';

// ============================================================================
// Message Types
// ============================================================================

/**
 * Message types for iframe communication between Tellet chat and parent page
 * @see backend/libs/common/src/types/conversation.types.ts for backend type alignment
 */
export enum MessageType {
  // Legacy message types (backward compatibility)
  INPUT = 'INPUT',
  OUTPUT = 'OUTPUT',
  END = 'END',

  // New enhanced message types
  STATUS_CHANGE = 'STATUS_CHANGE',
  CONVERSATION_COMPLETE = 'CONVERSATION_COMPLETE',
  DIGEST_READY = 'DIGEST_READY',
  CODING_READY = 'CODING_READY',

  // TELV1-422 — standardised, host-facing event names that wrap the internal
  // status events above. `tellet:processing` = "respondent finished; analysis
  // is running in the background; you may let them continue" (non-blocking).
  // `tellet:ready` = "digest + coded categories are now available". Both are
  // additive — the legacy events keep firing for backward compatibility.
  TELLET_PROCESSING = 'tellet:processing',
  TELLET_READY = 'tellet:ready',

  // TELV1-421 — embed cross-checkpoint pause/resume.
  // `CHECKPOINT_REACHED` (Tellet → host): the conversation paused at a
  // researcher-defined checkpoint; the host may advance its own survey and
  // bring the respondent back to a later iframe to continue.
  // `CHECKPOINT_COMPLETE` (host → Tellet): optional signal to resume in place
  // without a reload (a fresh iframe load of a paused conversation also resumes).
  CHECKPOINT_REACHED = 'CHECKPOINT_REACHED',
  CHECKPOINT_COMPLETE = 'CHECKPOINT_COMPLETE',
}

/**
 * Base interface for all iframe messages
 */
export interface IframeMessage {
  type: MessageType;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  data?: Record<string, any>;
}

// ============================================================================
// Conversation Status (aligned with backend)
// ============================================================================

/**
 * Conversation status enum - aligned with backend ConversationStatus
 * @see backend/libs/common/src/types/conversation.types.ts
 */
export enum ConversationStatus {
  TEST = 'test',
  SANDBOX = 'sandbox',
  PRESCREENING = 'prescreening',
  PRESCREENING_FAILED = 'prescreening_failed',
  SANDBOX_PRESCREENING_FAILED = 'sandbox_prescreening_failed',
  INITIATED = 'initiated',
  IN_PROGRESS = 'in_progress',
  DONE = 'done',
  ABANDONED = 'abandoned',
  ANALYZING = 'analyzing',
  DIGESTED = 'digested',
  // TELV1-450 — terminal status for a preview (test/sandbox) conversation that
  // ran the full pipeline; treated like DIGESTED for firing iframe events.
  PREVIEW_DIGESTED = 'preview_digested',
  TEST_DONE = 'test_done',
  SANDBOX_DONE = 'sandbox_done',
  DELETED = 'deleted',
  RESETTING = 'resetting',
}

/**
 * Statuses that mean "the interview is over and the transcript is final" from
 * the embedding host's point of view — i.e. the statuses for which
 * CONVERSATION_COMPLETE must be emitted. Must cover every status the backend
 * reports as done=true in GET /chat/progress (done, analyzing, digested,
 * sandbox_done), otherwise a session whose first observed status is e.g.
 * `analyzing` (the analyzer cron flips done→analyzing within 30s) never emits
 * the transcript at all (TELV1-476). TEST_DONE is deliberately excluded:
 * dashboard test chats are not embedded.
 */
export const COMPLETION_STATUSES: readonly ConversationStatus[] = [
  ConversationStatus.DONE,
  ConversationStatus.SANDBOX_DONE,
  ConversationStatus.ANALYZING,
  ConversationStatus.DIGESTED,
];

export const isCompletionStatus = (status: string | null | undefined): boolean =>
  COMPLETION_STATUSES.includes(status as ConversationStatus);

// ============================================================================
// Enhanced Message Data Interfaces
// ============================================================================

/**
 * Progress information for status change events
 */
export interface ProgressInfo {
  count: number;
  limit: number;
  done: boolean;
}

/**
 * Data sent with STATUS_CHANGE message
 * Sent whenever conversation status changes
 */
export interface StatusChangeData {
  status: ConversationStatus;
  conversationId: string;
  timestamp: string; // ISO 8601 format
  progress: ProgressInfo;
}

/**
 * Message format for iframe communication
 * Simplified version of backend Message type, formatted for parent page consumption
 * @see backend/libs/common/src/types/conversation.types.ts Message class
 */
export interface IframeMessageData {
  // TELV1-297 — stable per-message id for idempotent host-side write-back.
  // `null` for legacy/edge messages that lack a stored _id.
  id: string | null;
  // TELV1-297 — the interview question this message answers. `null` for
  // intro/closing/clarifier/tool messages, and may reference a follow-up
  // not present in the conversation `questions[]` lookup.
  questionId: string | null;
  role: 'user' | 'assistant';
  text: string; // sanitized HTML or transcription text
  created_at: string; // ISO 8601 format
  inputMethod: 'text' | 'speech' | 'video' | 'image';
  hasMedia: boolean;
  mediaType?: 'audio' | 'video' | 'image';
  mediaCount?: number; // TELV1-297 — number of media items (already emitted by the backend)
  // Note: Media URLs are NOT included for security/size reasons
}

/**
 * Conversation metadata (sanitized for iframe communication)
 * @see backend/libs/common/src/types/conversation.types.ts ConversationMetadata
 */
export interface ConversationMetadata {
  // #18 — respondent_id is the PRIMARY documented identifier the backend echoes
  // (participant_id is the legacy alias). Both are surfaced.
  respondent_id?: string;
  participant_id?: string;
  language?: string;
  source?: string;
  [key: string]: string | number | undefined;
  // Only the host-contract keys above survive sanitizeMetadata's allowlist
  // (backend chat-utils.service.ts); other keys are dropped.
}

/**
 * Data sent with CONVERSATION_COMPLETE message
 * Sent when interview completes (status becomes DONE)
 */
export interface ConversationCompleteData {
  conversationId: string;
  status: ConversationStatus;
  metadata: ConversationMetadata;
  messages: IframeMessageData[];
  // ISO 8601 format, or null: the backend sends null when a timestamp is not
  // set on the conversation, and degraded payloads (transcript fetch failed)
  // carry null too. Hosts must not assume these are always parseable dates.
  finishedAt: string | null;
  createdAt: string | null;
  startedAt: string | null;
  question_index: number;
  // TELV1-297 — question lookup so a per-message `questionId` resolves to
  // readable text. Ordered by interview position; `{id, text}` only.
  questions: Array<{ id: string; text: string }>;
  // TELV1-297 — respondent number within the project (panel correlation).
  // #19 — populated on the first respondent message (INITIATED→IN_PROGRESS), not
  // only at DONE; reliably non-null in CONVERSATION_COMPLETE (fires at DONE). May
  // be null only on a pre-first-message iframe-data poll.
  respondentIndex: number | null;
  digest?: {
    // Optional: only present when status is DIGESTED
    summary: string;
    themes: string[];
    opportunities: string[];
    quotes: DigestedQuote[];
  };
  // TELV1-186: coding is now { label, quote? } per category.
  // The quote is the verbatim respondent text that justifies the
  // label, populated by the auto-categorize hook. Optional: legacy
  // conversations and graceful-degradation cases ship without a quote.
  // Per-segment checkpoint coding: this map may be PARTIAL before the final
  // analysis completes (segments coded at checkpoints land as they finish);
  // `codingComplete` marks the final state.
  coding?: Record<string, Array<{ label: string; quote?: string }>>;
  // TELV1-422 — whether auto-categorization is configured for this conversation
  // (pre-defined categories present). The iframe uses it to decide whether to
  // wait for coding before firing `tellet:ready` and showing the processing state.
  codingExpected?: boolean;
  // Per-segment checkpoint coding — true once EVERY question with predefined
  // categories has a non-empty assignment on this conversation. The iframe
  // gates `tellet:ready` on this instead of "any coding present".
  codingComplete?: boolean;
  // TELV1-528 — conversation-scoped token redeemable at GET /embed/results
  // AFTER this page (and the iframe with it) is gone. Also pushed to the
  // host through the INPUT storage bridge as a `results_token…` attribute.
  // Null when the feature is unconfigured; present-but-degraded payloads
  // carry it when the progress poll supplied one.
  results_token?: string | null;
  error?: string; // Present if data fetch failed
}

/**
 * Digested quote with importance score
 * @see backend/libs/common/src/types/conversation.types.ts DigestedQuote
 */
export interface DigestedQuote {
  quote: string;
  score: number; // 1-100
}

/**
 * Data sent with DIGEST_READY message
 * Sent when conversation analysis completes (status becomes DIGESTED)
 * @see backend/libs/common/src/types/conversation.types.ts ConversationDigest
 */
export interface DigestReadyData {
  conversationId: string;
  summary: string;
  themes: string[];
  opportunities: string[];
  quotes: DigestedQuote[];
}

/**
 * Coding entry for a single category assignment on a question.
 * TELV1-186: `quote` is the verbatim respondent text that justifies
 * the category — the parent survey can write it inline into the
 * respondent's embedded data so the qualitative evidence lives next
 * to the quant variables in the survey export.
 *
 * `quote` is OPTIONAL: legacy conversations and graceful-degradation
 * cases (AI hallucinated a quote that didn't appear in the transcript)
 * ship the label only.
 */
export interface CodingEntry {
  label: string;
  quote?: string;
}

/**
 * Data sent with CODING_READY message
 * Sent when auto-categorization results become available. May fire MORE THAN
 * ONCE per conversation: once per finished segment while the conversation is
 * paused at a checkpoint (per-segment real-time coding), and again when the
 * post-completion analysis finishes. `categories` is CUMULATIVE — every coded
 * question so far — so re-rendering from the latest payload is always correct.
 * Contains category labels (and optional supporting quotes) mapped by question ID.
 */
export interface CodingReadyData {
  conversationId: string;
  categories: Record<string, CodingEntry[]>;
  // True on the FINAL emission (every question with predefined categories is
  // coded). Hosts that only care about final coding should act when this is
  // true — or key off `tellet:ready`, whose semantics are unchanged.
  complete?: boolean;
}

/**
 * TELV1-422 — Data for the standardised `tellet:processing` event.
 * Fired once when the conversation finishes (DONE). Non-blocking: signals that
 * analysis is running in the background and the respondent may continue.
 */
export interface TelletProcessingData {
  conversationId: string;
  timestamp: string; // ISO 8601 format
}

/**
 * TELV1-422 — Data for the standardised `tellet:ready` event.
 * Fired once when the analysis a host might consume is available (digest, or
 * coding for coded embeds), or when the iframe's display cap elapses first.
 */
export interface TelletReadyData {
  conversationId: string;
  timestamp: string; // ISO 8601 format
  // True when the ~30s display cap elapsed before coding finished. The coded
  // data will land later and can be reconciled server-side by respondent id.
  codingPending?: boolean;
}

/**
 * TELV1-421 — Data for the CHECKPOINT_REACHED event (Tellet → host).
 * Fired once when the conversation pauses at a checkpoint (status stays
 * IN_PROGRESS). The host surfaces its own survey questions and returns the
 * respondent to a later iframe to continue.
 */
export interface CheckpointReachedData {
  conversationId: string;
  timestamp: string; // ISO 8601 format
  checkpointId: string; // stable checkpoint node id
  label: string; // researcher-facing checkpoint label
  ordinal: number; // position in checkpoint_markers
}

/**
 * TELV1-421 — Data for the CHECKPOINT_COMPLETE event (host → Tellet).
 * Optional: lets the host resume the conversation in place. A fresh iframe load
 * of a paused conversation resumes automatically, so this is not required.
 */
export interface CheckpointCompleteData {
  conversationId?: string;
  checkpointId?: string;
}

// ============================================================================
// Helper Functions
// ============================================================================

export const isInIframe = (): boolean => {
  return window.parent !== window && window.top !== window;
};

export const sendMessageToParent = (message: IframeMessage): void => {
  if (isInIframe()) {
    // Wildcard '*' is intentional and not a security issue in our threat model:
    //   - The chat widget is embedded by survey-platform customers in their own
    //     pages; the embedding parent IS the legitimate consumer of these events
    //     (STATUS_CHANGE, CONVERSATION_COMPLETE, DIGEST_READY) by design.
    //   - A "malicious framing" attacker would have to host their own page that
    //     embeds the iframe, at which point the respondent typed their answers
    //     INTO the attacker's page — the attacker already has the data.
    //   - Origin-locking would not block same-window third-party scripts on the
    //     customer's page from reading the messages (the only real remaining
    //     concern), so it adds no real defense.
    //   - The architectural fix for parent-page-script exfiltration would be a
    //     thin "completed" notification + authenticated API fetch from the
    //     customer's backend; out of scope here.
    // See TELV1-255 (PR #1447 closed) for the full discussion.
    // nosemgrep: javascript.browser.security.wildcard-postmessage-configuration.wildcard-postmessage-configuration
    window.parent.postMessage(message, '*');
  }
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const sendInputMessageToParent = (data: Record<string, any>): void => {
  sendMessageToParent({
    type: MessageType.INPUT,
    data,
  });
};

/**
 * Send STATUS_CHANGE message to parent page
 * Called whenever conversation status changes
 * @param data Status change information
 */
export const sendStatusChangeToParent = (data: StatusChangeData): void => {
  if (!data.conversationId || !data.status) {
    console.warn('[iframeUtils] sendStatusChangeToParent: Missing required fields', data);
    return;
  }

  sendMessageToParent({
    type: MessageType.STATUS_CHANGE,
    data,
  });
};

/**
 * Send CONVERSATION_COMPLETE message to parent page
 * Called when interview completes (status becomes DONE)
 * Note: Also sends legacy END message for backward compatibility
 * @param data Complete conversation information
 */
export const sendConversationCompleteToParent = (data: ConversationCompleteData): void => {
  if (!data.conversationId) {
    console.warn('[iframeUtils] sendConversationCompleteToParent: Missing conversationId', data);
    return;
  }

  // CONVERSATION_COMPLETE must go out before the legacy END message: hosts are
  // documented to advance their survey on END, which may tear down this iframe
  // (or the whole page) — the transcript has to be in the parent's hands by
  // then. The old END-first order lost a production transcript (TELV1-476).
  sendMessageToParent({
    type: MessageType.CONVERSATION_COMPLETE,
    data,
  });

  // Legacy END message last: this is the "safe to advance the survey" signal.
  sendMessageToParent({ type: MessageType.END });
};

/**
 * Send DIGEST_READY message to parent page
 * Called when conversation analysis completes (status becomes DIGESTED)
 * @param data Digest information (summary, themes, opportunities, quotes)
 */
export const sendDigestReadyToParent = (data: DigestReadyData): void => {
  if (!data.conversationId || !data.summary) {
    console.warn('[iframeUtils] sendDigestReadyToParent: Missing required fields', data);
    return;
  }

  sendMessageToParent({
    type: MessageType.DIGEST_READY,
    data,
  });
};

/**
 * Send CODING_READY message to parent page
 * Called when auto-categorization completes (shortly after DIGEST_READY)
 * @param data Coding results (category labels per question)
 */
export const sendCodingReadyToParent = (data: CodingReadyData): void => {
  if (!data.conversationId || !data.categories) {
    console.warn('[iframeUtils] sendCodingReadyToParent: Missing required fields', data);
    return;
  }

  sendMessageToParent({
    type: MessageType.CODING_READY,
    data,
  });
};

/**
 * TELV1-422 — Send the standardised `tellet:processing` event.
 * Fired once when the conversation finishes (DONE). Non-blocking: signals that
 * analysis is running in the background and the respondent may continue.
 */
export const sendTelletProcessingToParent = (data: TelletProcessingData): void => {
  if (!data.conversationId) {
    console.warn('[iframeUtils] sendTelletProcessingToParent: Missing conversationId', data);
    return;
  }

  sendMessageToParent({
    type: MessageType.TELLET_PROCESSING,
    data,
  });
};

/**
 * TELV1-422 — Send the standardised `tellet:ready` event.
 * Fired once when the analysis a host might consume is available (digest, or
 * coding for coded embeds), or when the iframe's display cap elapses first.
 */
export const sendTelletReadyToParent = (data: TelletReadyData): void => {
  if (!data.conversationId) {
    console.warn('[iframeUtils] sendTelletReadyToParent: Missing conversationId', data);
    return;
  }

  sendMessageToParent({
    type: MessageType.TELLET_READY,
    data,
  });
};

/**
 * TELV1-421 — Send CHECKPOINT_REACHED to the host when the conversation pauses
 * at a checkpoint. The host uses it to advance its own survey and bring the
 * respondent back to a later iframe.
 */
export const sendCheckpointReachedToParent = (data: CheckpointReachedData): void => {
  if (!data.conversationId || !data.checkpointId) {
    console.warn('[iframeUtils] sendCheckpointReachedToParent: Missing required fields', data);
    return;
  }
  sendMessageToParent({
    type: MessageType.CHECKPOINT_REACHED,
    data,
  });
};

// Store data received from parent
// eslint-disable-next-line @typescript-eslint/no-explicit-any
let parentData: Record<string, any> = {};

// M6 — has the host replied to at least one storage-bridge request this page
// load? The reads that decide reload-resume (consent, current_conversation_id,
// screening_state) are synchronous, but the host's replay is async — so on a
// fresh iframe load they run before the reply lands and the respondent is bounced
// back to consent / a new conversation. `whenParentDataReady()` lets the boot
// sequence await one round-trip before those reads (see ChatPage hydration gate).
let parentDataReceived = false;
let bridgeReadyWaiters: Array<() => void> = [];

// TELV1-421 — registered handler for CHECKPOINT_COMPLETE from the host.
let checkpointCompleteHandler: ((data: CheckpointCompleteData) => void) | null = null;

/**
 * TELV1-421 — register a handler for the host's optional CHECKPOINT_COMPLETE
 * signal (resume in place). Returns an unsubscribe function.
 */
export const onCheckpointComplete = (
  handler: (data: CheckpointCompleteData) => void,
): (() => void) => {
  checkpointCompleteHandler = handler;
  return () => {
    if (checkpointCompleteHandler === handler) checkpointCompleteHandler = null;
  };
};

// H6/H7 — the host replays the iframe's OWN HTML attributes alongside the
// Tellet storage keys (the storage bridge stores each value as an iframe
// attribute, so `Array.from(frame.attributes)` echoes id/src/allow/... back
// too). These are noise and are never read via getParentData; drop them from
// the merge so parentData holds only bridge storage state. Matching is
// opportunistic — an unknown attribute simply stays (harmless, as before);
// Tellet keys are never HTML attribute names so they are never dropped.
const IFRAME_HTML_ATTRS = new Set<string>([
  'id',
  'src',
  'srcdoc',
  'name',
  'sandbox',
  'allow',
  'allowfullscreen',
  'allowpaymentrequest',
  'width',
  'height',
  'referrerpolicy',
  'loading',
  'title',
  'class',
  'style',
  'frameborder',
  'framespacing',
  'scrolling',
  'marginwidth',
  'marginheight',
  'align',
  'hspace',
  'vspace',
  'longdesc',
  'csp',
  'fetchpriority',
  'credentialless',
  'browsingtopics',
  'role',
  'tabindex',
  'dir',
  'lang',
  'hidden',
  'slot',
  'part',
  'nonce',
  'translate',
  'draggable',
]);

// Base tokens of the storage keys that legitimately flow inbound through the
// bridge (each is suffixed with projectStorageSuffix and encodeKey()-encoded in
// transit). Used ONLY to recognise a would-have-been-consumed message for
// telemetry when the origin gate rejects it — NOT for filtering the merge, so a
// missing entry only means a rejected legit-looking message goes unflagged.
const BRIDGE_STORAGE_KEY_PREFIXES = [
  'current_conversation_id',
  'project_consent',
  'chat_settings',
  'screening_state',
  'screening_question_index',
  'current_project_language',
  'use_google_translate',
  'client_ip',
  'last_user_message',
  'permissionModalShown',
];

export const looksLikeBridgePayload = (data: Record<string, unknown>): boolean =>
  data.type === MessageType.CHECKPOINT_COMPLETE ||
  'clearAll' in data ||
  // The base tokens contain no '/', so encodeKey() leaves them intact at the
  // start of the (suffixed) key — match the raw inbound key directly.
  Object.keys(data).some((key) =>
    BRIDGE_STORAGE_KEY_PREFIXES.some((prefix) => key.startsWith(prefix)),
  );

export interface InboundBridgeDecision {
  // Keys to merge into parentData, or null when nothing should be merged.
  merge: Record<string, unknown> | null;
  // Whether to dispatch CHECKPOINT_COMPLETE to the registered handler.
  dispatchCheckpoint: boolean;
  // Whether to emit a telemetry breadcrumb (a bridge-looking message was
  // rejected by the origin gate — worth seeing).
  flagRejected: boolean;
}

/**
 * H6/H7 — pure decision for an inbound postMessage. `acceptedFromParent` is the
 * window-topology gate (`isInIframe() && event.source === window.parent`),
 * injected so this stays unit-testable without mocking window internals.
 *
 * When rejected (message not from the direct embedding parent) nothing is merged
 * or dispatched; only a telemetry flag is raised if the message LOOKED like a
 * real bridge replay (so a legit host wrongly rejected — e.g. an exotic nested
 * embed — is visible, while extension/HMR noise stays silent). When accepted, a
 * CHECKPOINT_COMPLETE is dispatched (control message, not storage), otherwise the
 * flat storage replay is merged with the iframe's own echoed HTML attributes and
 * the message-type marker dropped.
 */
export const decideInboundBridge = (
  data: Record<string, unknown>,
  acceptedFromParent: boolean,
): InboundBridgeDecision => {
  if (!acceptedFromParent) {
    return {
      merge: null,
      dispatchCheckpoint: false,
      flagRejected: looksLikeBridgePayload(data),
    };
  }

  if (data.type === MessageType.CHECKPOINT_COMPLETE) {
    return { merge: null, dispatchCheckpoint: true, flagRejected: false };
  }

  const merge: Record<string, unknown> = {};
  Object.keys(data).forEach((key) => {
    if (key === 'type') return;
    const lower = key.toLowerCase();
    if (IFRAME_HTML_ATTRS.has(lower)) return;
    if (lower.startsWith('aria-') || lower.startsWith('data-')) return;
    merge[key] = data[key];
  });
  return { merge, dispatchCheckpoint: false, flagRejected: false };
};

// Handle messages from parent (OUTPUT storage replay + CHECKPOINT_COMPLETE).
export const handleParentMessage = (event: MessageEvent): void => {
  if (!event.data || typeof event.data !== 'object') return;
  const data = event.data as Record<string, unknown>;

  // H6/H7 — origin gate. Only the DIRECT embedding parent may drive the storage
  // bridge / checkpoint resume. `event.source` is browser-populated and cannot
  // be forged: a sibling iframe, a nested frame, or a cross-origin ancestor
  // injecting current_conversation_id (→ resume a victim's conversation),
  // project_consent, or screening_state has source !== window.parent. Consistent
  // with the outbound TELV1-255 decision — we still don't defend against a
  // malicious DIRECT parent. Inert for the standalone flow (isInIframe() false →
  // parentData is never read there anyway).
  const acceptedFromParent = isInIframe() && event.source === window.parent;
  const { merge, dispatchCheckpoint, flagRejected } = decideInboundBridge(data, acceptedFromParent);

  // M6 — a genuine reply from the parent (storage replay or checkpoint control)
  // means the bridge is live; release anyone awaiting hydration.
  if (acceptedFromParent) {
    parentDataReceived = true;
    if (bridgeReadyWaiters.length) {
      const waiters = bridgeReadyWaiters;
      bridgeReadyWaiters = [];
      waiters.forEach((resolve) => resolve());
    }
  }

  if (flagRejected) {
    Sentry.addBreadcrumb({
      category: 'iframe.bridge',
      level: 'warning',
      message: 'Rejected inbound bridge message from a non-parent source',
      data: { keys: Object.keys(data).slice(0, 20) },
    });
  }
  if (merge) {
    parentData = { ...parentData, ...merge };
  }
  if (dispatchCheckpoint) {
    checkpointCompleteHandler?.(
      (data.data as CheckpointCompleteData) || (data as CheckpointCompleteData),
    );
  }
};

// Get data that was sent from parent
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const getParentData = (key: string): any => {
  // Notify parent that we are requesting data
  sendMessageToParent({ type: MessageType.OUTPUT });
  return parentData[key] || null;
};

/**
 * M6 — resolve once the host has replayed storage state (so the synchronous
 * getItem reads that follow see the persisted values), or after `timeoutMs` as
 * a fallback for hosts without the communication script (which never reply — in
 * that case reload-resume was never possible and we proceed exactly as before).
 *
 * A synchronous no-op outside an iframe (standalone/interview flow reads real
 * localStorage), and resolves immediately if a reply already arrived. Requests a
 * fresh OUTPUT replay so it works even before any getItem has triggered one.
 */
export const whenParentDataReady = (timeoutMs = 500): Promise<void> => {
  if (!isInIframe() || parentDataReceived) return Promise.resolve();
  sendMessageToParent({ type: MessageType.OUTPUT });
  return new Promise<void>((resolve) => {
    let settled = false;
    // #24 — re-request OUTPUT on a short interval until the host replies. If the
    // host attaches its message listener slightly after the iframe loads, a
    // single request can be missed and resume would be silently lost; polling
    // gives the late listener several chances within the timeout window.
    const poll = setInterval(() => {
      if (!parentDataReceived) sendMessageToParent({ type: MessageType.OUTPUT });
    }, 100);
    const finish = (): void => {
      if (settled) return;
      settled = true;
      clearInterval(poll);
      resolve();
    };
    bridgeReadyWaiters.push(finish);
    setTimeout(finish, timeoutMs);
  });
};

// Encode and decode keys to avoid issues with special characters when using setAttribute in iframes.
// #12 — lowercase the key: the host stores each value as an iframe ATTRIBUTE and
// the DOM lowercases attribute names, so it echoes them back lowercased. Without
// lowercasing on our side, a key with any uppercase char (e.g. a camelCase
// `source` suffix like `_forstaDecipher`) never round-trips and reload-resume
// silently starts a fresh conversation. Both setItem and getItem run through
// encodeKey, so lowercasing stays symmetric.
export function encodeKey(key: string): string {
  return key.replace(/\//g, '__slash__').toLowerCase();
}

export function decodeKey(encoded: string): string {
  return encoded.replace(/__slash__/g, '/');
}

// Initialize message listener
if (typeof window !== 'undefined') {
  window.addEventListener('message', handleParentMessage);
}
